import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, Icon, Modal, notification, Tooltip } from 'antd'
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import { FilterMatchMode } from 'primereact/api'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import 'primereact/resources/primereact.css'
import 'primereact/resources/themes/lara-light-indigo/theme.css'
import { Toolbar } from 'primereact/toolbar'

import ExportButton from '../../components/CustomButtons/exportGreenButton'
import GlobalSearchInput from '../../components/CustomGlobalSearchInput/globalSearchInput'
import { exportExcel } from '../../util/exportUtils'
import { api, request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import { isGR, isRtv } from '../../util/profiles'
import CreateCustomerLocation from './CreateLocation'

const ListLocations = function ({ customerId }) {
  const [locationsList, setLocationsList] = useState([])
  const [openLocation, setOpenLocation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isModalLocationVisible, setIsModalLocationVisible] = useState(false)
  const currentUser = useSelector((state) => state.auth.auth)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    locationName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    documentNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    addressDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
    cityStateName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phoneNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    regionName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    districtName: { value: null, matchMode: FilterMatchMode.CONTAINS }
  })
  const dt = useRef(null)

  useEffect(() => {
    if (customerId > 0) {
      listCustomerLocationsByCustomerId(customerId)
    }
  }, [])

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    let _filters = { ...filters }

    _filters['global'].value = value

    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  async function removeLocation(location_id) {
    const response = await api.destroyLocation(location_id)

    if (response) {
      notification.error({
        description: 'Este local não pode ser removido porque está vinculado a outros dados.',
        message: getMessage('sweetAlerts.warning')
      })
    } else {
      notification.success({
        description: 'Local removido com sucesso.',
        message: getMessage('sweetAlerts.success')
      })

      await listCustomerLocationsByCustomerId(customerId)
    }
  }

  async function listCustomerLocationsByCustomerId(customerId) {
    const { regions: memberRegions, districts: memberDistricts } = currentUser

    let params = {}
    if (isRtv()) {
      params.districtId = memberDistricts.map(({ id }) => id)
    } else if (isGR()) {
      params.regionId = memberRegions.map(({ id }) => id)
    }
    const items = await request(api.listCustomerLocations(customerId, { params }))
    setLocationsList(items)
    setLoading(false)
  }

  const rowClass = (items) => {
    return {
      'text-gray-500': !items.active
    }
  }

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly })
  }

  const exportExcelData = () => {
    exportExcel(locationsList, 'lista_de_locais_do_agricultor')
  }

  const renderHeader = () => {
    const leftContents = (
      <h2>
        <b>Locais do Agricultor</b>
      </h2>
    )
    const rightContents = (
      <div className="container-header-lists">
        <GlobalSearchInput value={globalFilterValue} onChange={onGlobalFilterChange} />
        <div>
          <ExportButton iconType="csv" label={getMessage('label.exportCvs')} onClick={() => exportCSV(false)} />
          <ExportButton iconType="excel" label={getMessage('label.exportXls')} onClick={exportExcelData} />
          <Button type="dashed" style={{ gap: '5px', color: 'white' }} onClick={() => setModalVisible(true)}>
            <Icon type="plus" size="small" style={{ color: '#fff', fontSize: 12 }} />
            <span style={{ fontWeight: '500', fontSize: '12px' }}>{getMessage('addLocal')}</span>
          </Button>
        </div>

        {renderModalLocation()}
      </div>
    )

    return <Toolbar start={leftContents} end={rightContents} />
  }

  const header = renderHeader()

  async function setModalVisible(visible, locationId) {
    setIsModalLocationVisible(visible)
    setOpenLocation(locationsList.find(({ id }) => id === locationId))
    await listCustomerLocationsByCustomerId(customerId)
  }

  function renderModalLocation() {
    return (
      <Modal
        title={<strong>{getMessage('createLocalAndContact')}</strong>}
        footer={null}
        destroyOnClose={() => {
          setIsModalLocationVisible(false)
          setOpenLocation(null)
        }}
        onCancel={() => {
          setIsModalLocationVisible(false)
          setOpenLocation(null)
        }}
        width="80%"
        visible={isModalLocationVisible}
      >
        <CreateCustomerLocation
          currentStep={0}
          customerId={customerId}
          local={openLocation}
          setIsModalLocationVisible={setModalVisible}
        />
      </Modal>
    )
  }

  const editBodyTemplate = ({ id }) => {
    return (
      <div style={{ display: 'flex' }}>
        <Tooltip title="Editar Local">
          <Button type="ghost" onClick={() => setModalVisible(true, id)}>
            <Icon type="edit" size="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Deletar Local">
          <Button onClick={() => removeLocation(id)}>
            <Icon type="delete" size="small" />
          </Button>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="card">
      <DataTable
        ref={dt}
        loading={loading}
        header={header}
        filters={filters}
        value={locationsList}
        rowClassName={rowClass}
        onSelectionChange={(e) => onLocationSelect(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        dataKey="id"
        className="p-datatable-scrollable"
        scrollHeight="70vh"
        size="small"
        emptyMessage="No locations found."
        filterDisplay="row"
        globalFilterFields={[
          'locationName',
          'documentNumber',
          'addressDescription',
          'cityStateName',
          'phoneNumber',
          'regionName',
          'districtName'
        ]}
        scrollable
      >
        <Column
          header="Nome"
          field="locationName"
          filterField="locationName"
          showFilterMenu={false}
          style={{ minWidth: '11rem', maxWidth: '12rem' }}
          filter
        />
        <Column
          header="Documento"
          field="documentNumber"
          filterField="documentNumber"
          showFilterMenu={false}
          style={{ width: '10rem' }}
          filter
        />
        <Column
          header="Endereço"
          field="addressDescription"
          filterField="addressDescription"
          showFilterMenu={false}
          bodyStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          style={{ minWidth: '10rem', maxWidth: '12rem' }}
          filter
        />
        <Column
          header="Cidade"
          field="cityStateName"
          filterField="cityStateName"
          showFilterMenu={false}
          style={{ width: '10rem' }}
          filter
        />
        <Column
          header="Contato"
          field="phoneNumber"
          filterField="phoneNumber"
          showFilterMenu={false}
          style={{ width: '10rem' }}
          filter
        />
        <Column
          header="Regional"
          field="regionName"
          filterField="regionName"
          showFilterMenu={false}
          style={{ minWidth: '10rem"' }}
          filter
        />
        <Column
          header="Distrito"
          field="districtName"
          filterField="districtName"
          showFilterMenu={false}
          style={{ minWidth: '10rem"' }}
          filter
        />
        <Column header="Ações" body={editBodyTemplate} />
      </DataTable>
    </div>
  )
}
export default ListLocations
